import { Form } from '@repo/ui';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { apiPost } from '@repo/lib/axios/axios-utils';
import { endpoints } from '@/lib/axios/axios-global';
import { useState } from 'react';
import TextInputForm from '@repo/ui/components/forms/TextInputForm';
import UnderlinePlusHeading from '@repo/ui/components/heading/UnderlinePlusHeading';
import FileUploadCard from '../forms/FileUploadCard';
import {
    AddStudentFormValues,
    AddStudentSchema,
} from '@/features/studentRelations/forms/schemas/add-student-schema';
import SuccessPopup from '@repo/ui/components/Popups/SuccessPopup';
import CancelPopup from '@repo/ui/components/Popups/CancelPopup';
import { PrimaryButton } from '@repo/ui/components/buttons/PrimaryButton';
import PlusIcon from '@repo/ui/components/icons/popups/PlusIcon';



import type { CustomerModalProps } from '../../types/component-props.types';
export default function StudentModal({ isOpen, onClose }: CustomerModalProps) {
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [showCancelPopup, setShowCancelPopup] = useState(false);

    const form = useForm<AddStudentFormValues>({
        resolver: zodResolver(AddStudentSchema),
        defaultValues: {
            first_name: '',
            last_name: '',
            phone: '',
            email: '',
            address: '',
            national_id: '',
            national_id_image_front: '',
            national_id_image_back: '',
        },
    });

    const onSubmit = async (data: AddStudentFormValues) => {
        setLoading(true);
        setError(null);
        try {
            const formData = new FormData();
            formData.append('first_name', data.first_name);
            formData.append('last_name', data.last_name);
            formData.append('phone', data.phone);
            formData.append('email', data.email);
            formData.append('address', data.address);
            formData.append('national_id', data.national_id);
            if (data.national_id_image_front instanceof File) {
                formData.append('national_id_image_front', data.national_id_image_front);
            }
            if (data.national_id_image_back instanceof File) {
                formData.append('national_id_image_back', data.national_id_image_back);
            }

            const response = await apiPost({
                endpoint: endpoints.student.create,
                body: formData,
                config: { headers: { 'Content-Type': 'multipart/form-data' } },
            });

            setShowSuccessPopup(true);
        } catch (error: any) {
            console.error('Error creating customer:', error);
            if (error.response?.data?.errors) {
                Object.entries(error.response.data.errors).forEach(([field, messages]) => {
                    form.setError(field as any, {
                        type: 'manual',
                        message: (messages as string[]).join(', '),
                    });
                });
            } else {
                let errorMessage = 'حدث خطأ أثناء إرسال البيانات. يرجى المحاولة مرة أخرى.';
                if (error.response) {
                    errorMessage = error.response.data.msg || errorMessage;
                } else if (error.request) {
                    errorMessage = 'تعذر الاتصال بالخادم. يرجى التحقق من اتصالك بالإنترنت.';
                }
                setError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleAddAnother = () => {
        setShowSuccessPopup(false);
        form.reset({
            first_name: '',
            last_name: '',
            phone: '',
            email: '',
            address: '',
            national_id: '',
            national_id_image_front: undefined,
            national_id_image_back: undefined,
        });
    };

    const handleCloseSuccess = () => {
        setShowSuccessPopup(false);
        onClose(); // Close modal without navigation
    };

    const handleCloseCancel = () => {
        setShowCancelPopup(false);
        onClose(); // Close modal without navigation
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="relative bg-white-blue-100 rounded-[12px] md:p-8 p-6 w-full max-w-2xl shadow-lg mx-3 h-[650px] overflow-y-auto">
                <Form {...form}>
                    <div className="flex flex-col gap-4">
                        <UnderlinePlusHeading text="اضافة طالب" onClick={onClose} icon={PlusIcon} />
                        <div className="flex flex-col md:gap-4 sm:gap-[14px] gap-3">
                            <div className="flex flex-col gap-4">
                                <FileUploadCard name="national_id_image_front" label="صورة الهوية الامامية" />
                                <FileUploadCard name="national_id_image_back" label="صورة الهوية الخلفية" />
                            </div>
                            <div className="flex md:flex-row flex-col gap-4">
                                <TextInputForm
                                    className="w-full"
                                    name="first_name"
                                    label="ادخل اسم الطالب"
                                    placeholder="الاسم الاول"
                                />
                                <TextInputForm
                                    className="w-full"
                                    name="last_name"
                                    label="ادخل اسم الطالب"
                                    placeholder="اسم الاخير"
                                />
                            </div>
                            <div className="flex md:flex-row flex-col gap-4">
                                <TextInputForm
                                    name="address"
                                    label="العنوان"
                                    placeholder="ادخل العنوان"
                                    className="w-full"
                                />
                                <TextInputForm
                                    name="national_id"
                                    label="رقم الهوية"
                                    placeholder="ادخل رقم الهوية"
                                    className="w-full"
                                />
                            </div>
                            <div className="flex md:flex-row flex-col gap-4">
                                <TextInputForm
                                    name="email"
                                    label="البريد الالكتروني"
                                    placeholder="ادخل البريد الالكتروني"
                                    className="w-full"
                                />
                                <TextInputForm
                                    label="رقم الهاتف"
                                    placeholder="ادخل رقم الهاتف"
                                    className="[&>input]:text-end [&>input]:placeholder:text-end w-full"
                                    name="phone"
                                    type="tel"
                                />
                            </div>
                            {error && (
                                <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                                    <div className="flex justify-between items-center">
                                        <span>{error}</span>
                                        <button
                                            onClick={() => setError(null)}
                                            className="text-red-700 hover:text-red-900"
                                        >
                                            ×
                                        </button>
                                    </div>
                                </div>
                            )}
                            <div className="mt-6 flex justify-between gap-4">
                                <button
                                    type="button"
                                    onClick={() => setShowCancelPopup(true)}
                                    className="py-2 text-[16px] font-medium text-red-800 bg-red-200 rounded-md hover:bg-red-300 w-full"
                                >
                                    إلغاء
                                </button>
                                <PrimaryButton
                                    type="button"
                                    className="xl:px-[107px] xl:py-[18px] xl:font-bold ms-6"
                                    disabled={loading}
                                    onClick={form.handleSubmit(onSubmit)}
                                >
                                    {loading ? 'جاري التحميل...' : 'اضافة'}
                                </PrimaryButton>
                            </div>
                        </div>
                    </div>
                </Form>
                <CancelPopup
                    open={showCancelPopup}
                    onClickButton1={() => setShowCancelPopup(false)}
                    onClickButton2={handleCloseCancel}
                    text1="إلغاء إضافة طالب"
                    text2="أنت على وشك إلغاء عملية إضافة طالب جديد دون إكمال البيانات المطلوبة. سيتم تجاهل جميع البيانات المدخلة، هل أنت متأكد؟"
                    textButton1="إكمال الطلب"
                    textButton2="إلغاء الطلب"
                />
                <SuccessPopup
                    open={showSuccessPopup}
                    text1="تم إضافة طالب جديد بنجاح"
                    text2="تم تسجيل بيانات الطالب الجديد بنجاح."
                    textButton1="العودة لقائمة الطلاب"
                    textButton2="إضافة طالب أخر"
                    onAddAnother={handleAddAnother}
                    onReturnToList={handleCloseSuccess}
                />
            </div>
        </div>
    );
}
